import { ConfirmDialog, NewConvergeButton } from '../../../presentation';
import {
  SubmissionStatus,
  SubmissionSubStatus,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';
import { useConvergeNavigation, useConvergeSnackBar } from '../../../contexts';

import { ComponentProps } from 'react';
import { LoadingButton } from '@mui/lab';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';

interface IReferredSubmissionButtonProps
  extends ComponentProps<typeof LoadingButton> {
  referenceId?: string;
  submissionId: string;
}

export const ReferredSubmissionButton = ({
  referenceId,
  submissionId,
  ...props
}: IReferredSubmissionButtonProps) => {
  const {
    info: { allowReferSubmission, isReferred },
  } = useUnderwritingSubmissionStateContext();

  const { notify, closeSnackbar } = useConvergeSnackBar();
  const navigate = useConvergeNavigation();
  const { upsertSubmission, loading: isUpdatingSubmission } =
    useUpsertSubmissionMutation({}, 'Overview');
  const [showReferConfirmationModal, setShowReferConfirmationModal] =
    useState<boolean>(false);

  const onReferredSubmissionConfirmationEvent = useCallback(
    async (submissionUuid: string) => {
      notify((isReferred ? 'Unreferring' : 'Referring') + ' submission...', {
        variant: 'info',
      });

      setShowReferConfirmationModal(false);

      try {
        await upsertSubmission({
          variables: {
            details: {
              uuid: submissionUuid,
              status: isReferred
                ? SubmissionStatus.Evaluation
                : SubmissionStatus.Referred,
              subStatus: isReferred
                ? SubmissionSubStatus.EvaluationEditable
                : null,
            },
          },
        });

        closeSnackbar();

        notify('Submission ' + (isReferred ? 'unreferred' : 'referred'), {
          variant: 'success',
        });

        if (!isReferred) {
          navigate.underwriting();
        }
      } catch (cause) {
        closeSnackbar();
        notify(
          cause instanceof Error
            ? `Error referring submission: ${cause.message}`
            : 'Error referring submission',
          {
            variant: 'error',
          }
        );
      }
    },
    [closeSnackbar, isReferred, navigate, notify, upsertSubmission]
  );

  if (!allowReferSubmission && !isReferred) {
    return null;
  }

  return (
    <>
      <NewConvergeButton
        {...props}
        loading={isUpdatingSubmission}
        variant="contained"
        onClick={() => setShowReferConfirmationModal(true)}
        label={isReferred ? 'Unrefer' : 'Refer'}
      />

      <ConfirmDialog
        show={showReferConfirmationModal}
        title={(isReferred ? 'Unrefer' : 'Refer') + ' Submission'}
        content={`Are you sure you want to change status of submission ${referenceId} to referred?`}
        onCancel={() => setShowReferConfirmationModal(false)}
        onConfirm={() => onReferredSubmissionConfirmationEvent(submissionId)}
      />
    </>
  );
};
